// src/utils/remediationFixPlans.js
// Pure remediation/fix-plan generation helpers for Compliance.jsx.
// No API calls, React state, validation side effects, or workflow mutations.

import { asObject } from "./awsEvidenceContext";

import { normalizeText } from "./complianceFormatters";
import {
  buildGenericFixPlanSteps,
  buildGenericValidationText,
  inferProviderFromRule,
} from "./rulePlans/genericRulePlans";
import {
  buildIamFixPlanSteps,
  buildIamValidationText,
  isIamRule,
} from "./rulePlans/iamRulePlans";
import {
  buildS3FixPlanSteps,
  buildS3ValidationText,
  isS3Rule,
} from "./rulePlans/s3RulePlans";
import {
  buildCloudTrailFixPlanSteps,
  buildCloudTrailValidationText,
  isCloudTrailRule,
} from "./rulePlans/cloudTrailRulePlans";
import {
  buildConfigFixPlanSteps,
  buildConfigValidationText,
  isConfigRule,
} from "./rulePlans/configRulePlans";

import { buildEc2FixPlanSteps, buildEc2ValidationText, isEc2Rule } from "./rulePlans/ec2RulePlans";

export { inferProviderFromRule };

export function getRemediationSummary(item) {
  return (
    item?.reason_summary ||
    item?.remediation_hint ||
    item?.description ||
    item?.summary ||
    "This control condition needs review against the current source evidence and governing procedure."
  );
}


function getRuleId(item = {}) {
  return String(item?.rule_id || item?.ruleId || "").toUpperCase();
}

function hasPlanSteps(steps) {
  return Array.isArray(steps) && steps.length > 0;
}

export function isIdentityConnectorRule(ruleId = "", item = {}) {
  const rule = String(ruleId || item?.rule_id || item?.ruleId || "").toUpperCase();
  const source = normalizeText(
    item?.source ||
      item?.source_kind ||
      item?.sourceKind ||
      item?.stream ||
      item?.source_table ||
      item?.sourceTable ||
      item?.raw?.source ||
      item?.raw?.stream ||
      ""
  );
  const resourceType = normalizeText(item?.resource_type || item?.resourceType || item?.raw?.resource_type || "");

  return (
    rule.startsWith("GW_") ||
    rule.startsWith("OKTA_") ||
    source.includes("google_workspace") ||
    source.includes("okta") ||
    resourceType.includes("google_workspace") ||
    resourceType.includes("okta")
  );
}

function getIdentityProviderLabel(item = {}) {
  const rule = getRuleId(item);
  const source = normalizeText(item?.source || item?.stream || item?.raw?.source || item?.raw?.stream || "");

  if (rule.startsWith("OKTA_") || source.includes("okta")) return "Okta";
  if (rule.startsWith("GW_") || source.includes("google_workspace")) return "Google Workspace";
  return "Identity provider";
}

function getIdentityResourceLabel(item = {}) {
  return (
    item?.resource_id ||
    item?.resourceId ||
    item?.raw?.resource_id ||
    item?.raw?.resourceId ||
    "the affected identity resource"
  );
}

function getIdentityRuleAction(ruleId = "", providerLabel = "Identity provider") {
  const rule = String(ruleId || "").toUpperCase();

  if (rule === "GW_2SV_DISABLED") {
    return "Enable 2-Step Verification for the affected Google Workspace user or enforce the 2SV policy for the user's organizational unit.";
  }
  if (rule === "GW_UNUSED_USER") {
    return "Confirm ownership and either suspend, archive, or deprovision the unused Google Workspace account.";
  }
  if (rule === "GW_INACTIVE_ADMIN") {
    return "Remove admin privileges from the inactive Google Workspace admin or document an approved exception.";
  }
  if (rule === "GW_ADMIN_COUNT") {
    return "Review Google Workspace admin population and keep only approved administrators.";
  }
  if (rule === "GW_SUPER_ADMIN_EXPOSURE") {
    return "Review Super Admin assignments and reduce standing super-admin access to approved break-glass users only.";
  }
  if (rule === "OKTA_MFA_DISABLED") {
    return "Enroll the affected Okta user in MFA and enforce the appropriate sign-on policy.";
  }
  if (rule === "OKTA_UNUSED_ACCOUNT") {
    return "Confirm account ownership and deactivate or suspend unused Okta accounts.";
  }
  if (rule === "OKTA_STALE_ADMIN") {
    return "Review stale Okta administrators and remove admin privileges or document an approved exception.";
  }
  if (rule === "OKTA_ADMIN_EXPOSURE") {
    return "Review Okta admin population and keep only approved administrators.";
  }
  if (rule === "OKTA_PRIVILEGED_GROUP_EXPOSURE") {
    return "Review membership in privileged Okta groups and remove unapproved users.";
  }

  return `Review the ${providerLabel} identity finding and remediate the affected identity configuration.`;
}

export function buildIdentityFixPlanSteps(item = {}, relatedSops = [], task = {}) {
  const rule = getRuleId(item);
  const providerLabel = getIdentityProviderLabel(item);
  const resourceLabel = getIdentityResourceLabel(item);
  const action = getIdentityRuleAction(rule, providerLabel);
  const hasSop = Array.isArray(relatedSops) && relatedSops.length > 0;

  const sourceOpenStep =
    providerLabel === "Okta"
      ? "Open the Okta Admin Console."
      : "Open the Google Admin Console.";

  const navigationStep =
    providerLabel === "Okta"
      ? rule === "OKTA_PRIVILEGED_GROUP_EXPOSURE"
        ? `Go to Directory → Groups and open ${resourceLabel}.`
        : `Go to Directory → People and open ${resourceLabel}.`
      : rule === "GW_2SV_DISABLED"
      ? `Go to Directory → Users → ${resourceLabel} → Security and review 2-Step Verification.`
      : rule === "GW_SUPER_ADMIN_EXPOSURE" || rule === "GW_ADMIN_COUNT"
      ? "Go to Account → Admin roles and review assigned administrators."
      : `Go to Directory → Users and open ${resourceLabel}.`;

  const steps = [
    sourceOpenStep,
    navigationStep,
    action,
    "Capture before/after evidence showing the identity setting, role membership, group membership, or account state was corrected.",
    providerLabel === "Okta"
      ? "Rerun the Okta identity inventory sync, normalize identity findings, and refresh posture."
      : "Rerun the Google Workspace directory sync, normalize identity findings, and refresh posture.",
    "Attach evidence and validation output to the remediation task before marking it ready for validation.",
  ];

  if (hasSop) {
    steps.splice(2, 0, "Follow the linked governing SOP before making production identity or admin-role changes.");
  }

  return steps;
}

export function buildIdentityValidationText(item = {}) {
  const rule = getRuleId(item);
  const providerLabel = getIdentityProviderLabel(item);
  const resourceLabel = getIdentityResourceLabel(item);

  if (rule === "GW_2SV_DISABLED") {
    return `Validation passes when Google Workspace directory inventory shows 2-Step Verification is enrolled/enforced for ${resourceLabel}.`;
  }

  if (rule === "OKTA_MFA_DISABLED") {
    return `Validation passes when Okta identity inventory shows MFA factors are enrolled for ${resourceLabel}.`;
  }

  if (rule.includes("STALE_ADMIN") || rule.includes("INACTIVE_ADMIN")) {
    return `Validation passes when ${providerLabel} inventory shows no stale or inactive administrator exposure for ${resourceLabel}.`;
  }

  if (rule.includes("PRIVILEGED_GROUP")) {
    return `Validation passes when ${providerLabel} inventory shows privileged group membership is approved and reduced to required users only.`;
  }

  return `Validation passes when refreshed ${providerLabel} identity inventory no longer reports this finding as failed.`;
}

export function isCodeConnectorRule(ruleId = "", item = {}) {
  const rule = String(ruleId || item?.rule_id || item?.ruleId || "").toUpperCase();
  const source = normalizeText(
    item?.source ||
      item?.finding_source ||
      item?.stream ||
      item?.source_table ||
      item?.raw?.source ||
      item?.raw?.stream ||
      ""
  );
  const resourceType = normalizeText(item?.resource_type || item?.resourceType || item?.raw?.resource_type || "");

  return (
    rule.startsWith("GH_") ||
    source.includes("github") ||
    resourceType.includes("github") ||
    resourceType.includes("repository") ||
    resourceType.includes("branch")
  );
}

export function isEdgeConnectorRule(ruleId = "", item = {}) {
  const rule = String(ruleId || item?.rule_id || item?.ruleId || "").toUpperCase();
  const source = normalizeText(
    item?.source ||
      item?.finding_source ||
      item?.stream ||
      item?.source_table ||
      item?.raw?.source ||
      item?.raw?.stream ||
      ""
  );
  const resourceType = normalizeText(item?.resource_type || item?.resourceType || item?.raw?.resource_type || "");

  return rule.startsWith("CF_") || source.includes("cloudflare") || resourceType.includes("cloudflare");
}

function getCodeResourceLabel(item = {}) {
  return (
    item?.resource_id ||
    item?.resourceId ||
    item?.raw?.resource_id ||
    item?.raw?.resourceId ||
    item?.finding_metadata?.repository ||
    item?.raw?.finding_metadata?.repository ||
    "the affected repository"
  );
}

function getEdgeResourceLabel(item = {}) {
  return (
    item?.resource_id ||
    item?.resourceId ||
    item?.raw?.resource_id ||
    item?.raw?.resourceId ||
    item?.finding_metadata?.zone_name ||
    item?.raw?.finding_metadata?.zone_name ||
    "the affected Cloudflare zone"
  );
}

function getCodeRuleAction(ruleId = "", repository = "the affected repository") {
  const rule = String(ruleId || "").toUpperCase();

  if (rule === "GH_DEPENDABOT_DISABLED") {
    return `Enable Dependabot alerts for ${repository} and confirm repository security alerts are active.`;
  }
  if (rule === "GH_SECRET_SCANNING_DISABLED") {
    return `Enable GitHub secret scanning for ${repository}, or document why the plan/token cannot expose this setting.`;
  }
  if (rule === "GH_PUBLIC_REPOSITORY") {
    return `Confirm ${repository} must remain private, or document an approved exception if public exposure is intentional.`;
  }
  if (rule === "GH_BRANCH_PROTECTION_DISABLED") {
    return `Enable branch protection for the default branch of ${repository}.`;
  }
  if (rule === "GH_REQUIRED_REVIEWS_DISABLED") {
    return `Require pull request reviews before merge on the default branch of ${repository}.`;
  }
  if (rule === "GH_ADMIN_BYPASS_ENABLED") {
    return `Enforce branch protection for administrators on ${repository} or document a tightly controlled exception.`;
  }

  return `Review GitHub security settings for ${repository} and remediate the affected repository control.`;
}

export function buildCodeFixPlanSteps(item = {}, relatedSops = [], task = {}) {
  const rule = getRuleId(item);
  const repository = getCodeResourceLabel(item);
  const hasSop = Array.isArray(relatedSops) && relatedSops.length > 0;
  const steps = [
    "Open GitHub and navigate to the affected repository.",
    `Repository: ${repository}.`,
    getCodeRuleAction(rule, repository),
  ];

  if (rule === "GH_DEPENDABOT_DISABLED") {
    steps.push("Go to Settings → Code security and analysis and enable Dependabot alerts.");
  } else if (rule === "GH_SECRET_SCANNING_DISABLED") {
    steps.push("Go to Settings → Code security and analysis and enable secret scanning where available for the repository plan.");
  } else if (
    rule === "GH_BRANCH_PROTECTION_DISABLED" ||
    rule === "GH_REQUIRED_REVIEWS_DISABLED" ||
    rule === "GH_ADMIN_BYPASS_ENABLED"
  ) {
    steps.push("Go to Settings → Branches and open the default branch protection rule.");
    steps.push("Require pull request reviews, require status checks where applicable, and prevent bypass by administrators unless formally approved.");
  } else if (rule === "GH_PUBLIC_REPOSITORY") {
    steps.push("Go to Settings → General → Danger Zone and confirm repository visibility is Private, unless public visibility is explicitly approved.");
  }

  if (hasSop) {
    steps.splice(2, 0, "Follow the linked governing SOP before changing repository governance settings.");
  }

  steps.push("Capture before/after evidence from the GitHub repository settings page.");
  steps.push("Rerun the GitHub repository sync, normalize GitHub findings, and refresh posture.");
  steps.push("Attach evidence and validation output to the remediation task before marking it ready for validation.");

  return steps;
}

export function buildCodeValidationText(item = {}) {
  const rule = getRuleId(item);
  const repository = getCodeResourceLabel(item);

  if (rule === "GH_DEPENDABOT_DISABLED") {
    return `Validation passes when refreshed GitHub security settings show Dependabot alerts are enabled for ${repository}.`;
  }
  if (rule === "GH_SECRET_SCANNING_DISABLED") {
    return `Validation passes when refreshed GitHub security settings show secret scanning is enabled or the setting is documented as access-limited/unsupported for ${repository}.`;
  }
  if (rule === "GH_PUBLIC_REPOSITORY") {
    return `Validation passes when refreshed GitHub repository inventory shows ${repository} is private, or an approved public-repository exception is recorded.`;
  }
  if (rule === "GH_BRANCH_PROTECTION_DISABLED") {
    return `Validation passes when refreshed GitHub branch evidence shows default branch protection can be read and is enabled for ${repository}.`;
  }
  if (rule === "GH_REQUIRED_REVIEWS_DISABLED") {
    return `Validation passes when refreshed GitHub branch evidence shows required pull request reviews are enabled for ${repository}.`;
  }
  if (rule === "GH_ADMIN_BYPASS_ENABLED") {
    return `Validation passes when refreshed GitHub branch evidence shows administrators cannot bypass the required branch protection controls for ${repository}, or an approved exception exists.`;
  }

  return `Validation passes when refreshed GitHub evidence no longer reports this code governance finding as failed.`;
}

function getEdgeRuleAction(ruleId = "", zone = "the affected Cloudflare zone") {
  const rule = String(ruleId || "").toUpperCase();

  if (rule === "CF_WAF_DISABLED") {
    return `Enable Cloudflare WAF for ${zone} and review the managed ruleset baseline.`;
  }
  if (rule === "CF_TLS_MIN_VERSION_WEAK") {
    return `Raise the minimum TLS version for ${zone} to 1.2 or higher.`;
  }
  if (rule === "CF_ALWAYS_USE_HTTPS_DISABLED") {
    return `Enable Always Use HTTPS for ${zone}.`;
  }
  if (rule === "CF_SECURITY_LEVEL_LOW") {
    return `Set Cloudflare Security Level for ${zone} to Medium or higher unless an exception is approved.`;
  }
  if (rule === "CF_DNSSEC_DISABLED") {
    return `Enable DNSSEC for ${zone}, or fix token permissions so OASYS can verify DNSSEC status.`;
  }
  if (rule === "CF_ZONE_ACTIVITY_OBSERVED") {
    return `Keep Cloudflare zone inventory sync scheduled for ${zone}.`;
  }

  return `Review Cloudflare edge security settings for ${zone} and remediate the affected edge control.`;
}

export function buildEdgeFixPlanSteps(item = {}, relatedSops = [], task = {}) {
  const rule = getRuleId(item);
  const zone = getEdgeResourceLabel(item);
  const hasSop = Array.isArray(relatedSops) && relatedSops.length > 0;
  const steps = [
    "Open the Cloudflare dashboard.",
    `Zone: ${zone}.`,
    getEdgeRuleAction(rule, zone),
  ];

  if (rule === "CF_WAF_DISABLED") {
    steps.push("Go to Security → WAF and enable the WAF or managed ruleset baseline appropriate for the zone.");
  } else if (rule === "CF_TLS_MIN_VERSION_WEAK") {
    steps.push("Go to SSL/TLS → Edge Certificates and set Minimum TLS Version to 1.2 or higher.");
    steps.push("Validate that known clients and integrations still connect after raising the TLS floor.");
  } else if (rule === "CF_ALWAYS_USE_HTTPS_DISABLED") {
    steps.push("Go to SSL/TLS → Edge Certificates and turn on Always Use HTTPS.");
    steps.push("Validate HTTP requests redirect to HTTPS for the zone.");
  } else if (rule === "CF_SECURITY_LEVEL_LOW") {
    steps.push("Go to Security → Settings and set Security Level to Medium or higher.");
  } else if (rule === "CF_DNSSEC_DISABLED") {
    steps.push("Go to DNS → Settings → DNSSEC and enable DNSSEC if it is not already enabled.");
    steps.push("If the API still returns authentication errors, update the Cloudflare API token to include DNS read/DNSSEC visibility and rerun sync.");
  } else if (rule === "CF_ZONE_ACTIVITY_OBSERVED") {
    steps.push("Confirm the Cloudflare connector remains healthy and scheduled sync continues to collect zone evidence.");
  }

  if (hasSop) {
    steps.splice(2, 0, "Follow the linked governing SOP before making edge-security or DNS changes.");
  }

  steps.push("Capture before/after evidence from the Cloudflare setting page.");
  steps.push("Rerun the Cloudflare sync, normalize Cloudflare findings, and refresh posture.");
  steps.push("Attach evidence and validation output to the remediation task before marking it ready for validation.");

  return steps;
}

export function buildEdgeValidationText(item = {}) {
  const rule = getRuleId(item);
  const zone = getEdgeResourceLabel(item);

  if (rule === "CF_WAF_DISABLED") {
    return `Validation passes when refreshed Cloudflare WAF settings show WAF is enabled for ${zone}.`;
  }
  if (rule === "CF_TLS_MIN_VERSION_WEAK") {
    return `Validation passes when refreshed Cloudflare TLS settings show the minimum TLS version is 1.2 or higher for ${zone}.`;
  }
  if (rule === "CF_ALWAYS_USE_HTTPS_DISABLED") {
    return `Validation passes when refreshed Cloudflare TLS settings show Always Use HTTPS is enabled for ${zone}.`;
  }
  if (rule === "CF_SECURITY_LEVEL_LOW") {
    return `Validation passes when refreshed Cloudflare security settings show Security Level is medium or higher for ${zone}.`;
  }
  if (rule === "CF_DNSSEC_DISABLED") {
    return `Validation passes when refreshed Cloudflare DNS evidence confirms DNSSEC is enabled for ${zone}, or the finding is intentionally retained as access-limited with an approved exception.`;
  }
  if (rule === "CF_ZONE_ACTIVITY_OBSERVED") {
    return `Validation passes when Cloudflare zone inventory continues to be observed for ${zone}.`;
  }

  return `Validation passes when refreshed Cloudflare evidence no longer reports this edge security finding as failed.`;
}

export function getRulePlanFamily(item = {}, relatedSops = [], task = {}) {
  const rule = getRuleId(item);

  if (isIdentityConnectorRule(rule, item)) {
    const identitySteps = buildIdentityFixPlanSteps(item, relatedSops, task);
    if (hasPlanSteps(identitySteps)) return "identity";
  }

  if (isCodeConnectorRule(rule, item)) {
    const codeSteps = buildCodeFixPlanSteps(item, relatedSops, task);
    if (hasPlanSteps(codeSteps)) return "code";
  }

  if (isEdgeConnectorRule(rule, item)) {
    const edgeSteps = buildEdgeFixPlanSteps(item, relatedSops, task);
    if (hasPlanSteps(edgeSteps)) return "edge";
  }

  if (isEc2Rule(rule)) {
    const ec2Steps = buildEc2FixPlanSteps(item, relatedSops, task);
    if (hasPlanSteps(ec2Steps)) return "ec2";
  }

  if (isIamRule(rule)) {
    const iamSteps = buildIamFixPlanSteps(item, relatedSops, task);
    if (hasPlanSteps(iamSteps)) return "iam";
  }

  if (isS3Rule(rule)) {
    const s3Steps = buildS3FixPlanSteps(item, relatedSops, task);
    if (hasPlanSteps(s3Steps)) return "s3";
  }

  if (isCloudTrailRule(rule)) {
    const cloudTrailSteps = buildCloudTrailFixPlanSteps(item, relatedSops, task);
    if (hasPlanSteps(cloudTrailSteps)) return "cloudtrail";
  }

  if (isConfigRule(rule)) {
    const configSteps = buildConfigFixPlanSteps(item, relatedSops, task);
    if (hasPlanSteps(configSteps)) return "config";
  }

  return "generic";
}

export function getRulePlanDiagnostic(item = {}, relatedSops = [], task = {}) {
  const rule = getRuleId(item);
  const family = getRulePlanFamily(item, relatedSops, task);
  const provider = isIdentityConnectorRule(rule, item)
    ? getIdentityProviderLabel(item).toLowerCase().replace(/\s+/g, "_")
    : isCodeConnectorRule(rule, item)
    ? "github"
    : isEdgeConnectorRule(rule, item)
    ? "cloudflare"
    : inferProviderFromRule(rule);

  const labels = {
    ec2: "EC2-specific remediation plan",
    iam: "IAM-specific remediation plan",
    s3: "S3-specific remediation plan",
    cloudtrail: "CloudTrail-specific remediation plan",
    config: "AWS Config / drift-specific remediation plan",
    identity: "Identity connector remediation plan",
    code: "GitHub code governance remediation plan",
    edge: "Cloudflare edge security remediation plan",
    generic: "Standard remediation plan",
  };

  return {
    rule_id: rule,
    family,
    provider,
    label: labels[family] || labels.generic,
    is_generic_fallback: family === "generic",
  };
}

export function buildFixPlanSteps(item = {}, relatedSops = [], task = {}) {
  const rule = String(item?.rule_id || item?.ruleId || "").toUpperCase();

  if (isIdentityConnectorRule(rule, item)) {
    const identitySteps = buildIdentityFixPlanSteps(item, relatedSops, task);
    if (Array.isArray(identitySteps) && identitySteps.length) return identitySteps;
  }

  if (isCodeConnectorRule(rule, item)) {
    const codeSteps = buildCodeFixPlanSteps(item, relatedSops, task);
    if (Array.isArray(codeSteps) && codeSteps.length) return codeSteps;
  }

  if (isEdgeConnectorRule(rule, item)) {
    const edgeSteps = buildEdgeFixPlanSteps(item, relatedSops, task);
    if (Array.isArray(edgeSteps) && edgeSteps.length) return edgeSteps;
  }

  if (isEc2Rule(rule)) {
    const ec2Steps = buildEc2FixPlanSteps(item, relatedSops, task);
    if (Array.isArray(ec2Steps) && ec2Steps.length) return ec2Steps;
  }

  if (isIamRule(rule)) {
    const iamSteps = buildIamFixPlanSteps(item, relatedSops, task);
    if (Array.isArray(iamSteps) && iamSteps.length) return iamSteps;
  }

  if (isS3Rule(rule)) {
    const s3Steps = buildS3FixPlanSteps(item, relatedSops, task);
    if (Array.isArray(s3Steps) && s3Steps.length) return s3Steps;
  }

  if (isCloudTrailRule(rule)) {
    const cloudTrailSteps = buildCloudTrailFixPlanSteps(item, relatedSops, task);
    if (Array.isArray(cloudTrailSteps) && cloudTrailSteps.length) return cloudTrailSteps;
  }

  if (isConfigRule(rule)) {
    const configSteps = buildConfigFixPlanSteps(item, relatedSops, task);
    if (Array.isArray(configSteps) && configSteps.length) return configSteps;
  }

  return buildGenericFixPlanSteps(item, relatedSops, task);
}

export function buildValidationText(item = {}) {
  const rule = String(item?.rule_id || item?.ruleId || "").toUpperCase();

  if (isIdentityConnectorRule(rule, item)) {
    const identityText = buildIdentityValidationText(item);
    if (identityText) return identityText;
  }

  if (isCodeConnectorRule(rule, item)) {
    const codeText = buildCodeValidationText(item);
    if (codeText) return codeText;
  }

  if (isEdgeConnectorRule(rule, item)) {
    const edgeText = buildEdgeValidationText(item);
    if (edgeText) return edgeText;
  }

  if (isEc2Rule(rule)) {
    const ec2Text = buildEc2ValidationText(item);
    if (ec2Text) return ec2Text;
  }

  if (isIamRule(rule)) {
    const iamText = buildIamValidationText(item);
    if (iamText) return iamText;
  }

  if (isS3Rule(rule)) {
    const s3Text = buildS3ValidationText(item);
    if (s3Text) return s3Text;
  }

  if (isCloudTrailRule(rule)) {
    const cloudTrailText = buildCloudTrailValidationText(item);
    if (cloudTrailText) return cloudTrailText;
  }

  if (isConfigRule(rule)) {
    const configText = buildConfigValidationText(item);
    if (configText) return configText;
  }

  return buildGenericValidationText(item);
}

export function hasObjectKeys(value) {
  return Boolean(value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0);
}

export function findTopRiskContextForFixPlan(risks = [], item = {}, frameworkFallback = "") {
  if (!item) return null;

  const itemFramework = normalizeText(item.framework || frameworkFallback);
  const itemControl = normalizeText(item.control_id || item.controlId);
  const itemRule = normalizeText(item.rule_id || item.ruleId);
  const itemResource = normalizeText(item.resource_id || item.resourceId);

  return (
    risks.find((risk) => {
      if (!risk) return false;

      const riskFramework = normalizeText(risk.framework || frameworkFallback);
      const riskControl = normalizeText(risk.control_id || risk.controlId);
      const riskRule = normalizeText(risk.rule_id || risk.ruleId);
      const riskResource = normalizeText(risk.resource_id || risk.resourceId);

      if (itemFramework && riskFramework && itemFramework !== riskFramework) return false;
      if (itemControl && riskControl && itemControl !== riskControl) return false;
      if (itemRule && riskRule && itemRule !== riskRule) return false;
      if (itemResource && riskResource && itemResource !== riskResource) return false;

      return Boolean(itemControl || itemRule || itemResource);
    }) || null
  );
}

export function mergeFixPlanWithSourceContext(item = {}, sourceRisk = null) {
  if (!item) return item;

  const raw = asObject(item.raw);
  const source = asObject(sourceRisk);
  const sourceRaw = asObject(source.raw);

  const itemAwsContext = asObject(item.aws_context);
  const rawAwsContext = asObject(raw.aws_context);
  const sourceAwsContext = asObject(source.aws_context);
  const sourceRawAwsContext = asObject(sourceRaw.aws_context);

  const itemFindingMetadata = asObject(item.finding_metadata);
  const rawFindingMetadata = asObject(raw.finding_metadata);
  const sourceFindingMetadata = asObject(source.finding_metadata);
  const sourceRawFindingMetadata = asObject(sourceRaw.finding_metadata);

  return {
    ...item,
    resource_type:
      item.resource_type ||
      item.resourceType ||
      raw.resource_type ||
      raw.resourceType ||
      source.resource_type ||
      source.resourceType ||
      sourceRaw.resource_type ||
      sourceRaw.resourceType ||
      "",
    resource_id:
      item.resource_id ||
      item.resourceId ||
      raw.resource_id ||
      raw.resourceId ||
      source.resource_id ||
      source.resourceId ||
      sourceRaw.resource_id ||
      sourceRaw.resourceId ||
      "",
    aws_context: hasObjectKeys(itemAwsContext)
      ? itemAwsContext
      : hasObjectKeys(rawAwsContext)
      ? rawAwsContext
      : hasObjectKeys(sourceAwsContext)
      ? sourceAwsContext
      : hasObjectKeys(sourceRawAwsContext)
      ? sourceRawAwsContext
      : item.aws_context || null,
    finding_metadata: hasObjectKeys(itemFindingMetadata)
      ? itemFindingMetadata
      : hasObjectKeys(rawFindingMetadata)
      ? rawFindingMetadata
      : hasObjectKeys(sourceFindingMetadata)
      ? sourceFindingMetadata
      : hasObjectKeys(sourceRawFindingMetadata)
      ? sourceRawFindingMetadata
      : item.finding_metadata || null,
    raw: {
      ...source,
      ...raw,
      ...item.raw,
      aws_context: hasObjectKeys(rawAwsContext)
        ? rawAwsContext
        : hasObjectKeys(sourceAwsContext)
        ? sourceAwsContext
        : sourceRawAwsContext,
      finding_metadata: hasObjectKeys(rawFindingMetadata)
        ? rawFindingMetadata
        : hasObjectKeys(sourceFindingMetadata)
        ? sourceFindingMetadata
        : sourceRawFindingMetadata,
    },
  };
}

export function buildFixPlanItem(item = {}, source = "control") {
  const raw = asObject(item?.raw);
  const rawRaw = asObject(raw?.raw);

  const itemAwsContext = asObject(item?.aws_context);
  const rawAwsContext = asObject(raw?.aws_context);
  const nestedRawAwsContext = asObject(rawRaw?.aws_context);
  const itemFindingMetadata = asObject(item?.finding_metadata);
  const rawFindingMetadata = asObject(raw?.finding_metadata);
  const nestedRawFindingMetadata = asObject(rawRaw?.finding_metadata);

  const awsContext = Object.keys(itemAwsContext).length
    ? itemAwsContext
    : Object.keys(rawAwsContext).length
    ? rawAwsContext
    : Object.keys(nestedRawAwsContext).length
    ? nestedRawAwsContext
    : null;

  const findingMetadata = Object.keys(itemFindingMetadata).length
    ? itemFindingMetadata
    : Object.keys(rawFindingMetadata).length
    ? rawFindingMetadata
    : Object.keys(nestedRawFindingMetadata).length
    ? nestedRawFindingMetadata
    : null;

  return {
    source,
    framework: item.framework || raw.framework || rawRaw.framework || "",
    control_id: item.control_id || item.controlId || raw.control_id || raw.controlId || rawRaw.control_id || rawRaw.controlId || "",
    rule_id: item.rule_id || item.ruleId || raw.rule_id || raw.ruleId || rawRaw.rule_id || rawRaw.ruleId || "",
    resource_id: item.resource_id || item.resourceId || raw.resource_id || raw.resourceId || rawRaw.resource_id || rawRaw.resourceId || "",
    resource_type: item.resource_type || item.resourceType || raw.resource_type || raw.resourceType || rawRaw.resource_type || rawRaw.resourceType || "",
    state: item.state || item.status || item.control_state || item.freshness_status || raw.state || raw.status || raw.control_state || raw.freshness_status || "",
    freshness_state: item.freshness_state || item.freshness_status || item.freshness || raw.freshness_state || raw.freshness_status || raw.freshness || "",
    severity: item.severity || item.severity_weight || raw.severity || raw.severity_weight || "",
    score: item.score ?? raw.score,
    open_findings_total:
      item.open_findings_total ||
      item.findings_open ||
      item.open_findings ||
      raw.open_findings_total ||
      raw.findings_open ||
      raw.open_findings ||
      "",
    last_checked_at:
      item.last_checked_at ||
      item.captured_at ||
      item.updated_at ||
      item.last_seen_at ||
      raw.last_checked_at ||
      raw.captured_at ||
      raw.updated_at ||
      raw.last_seen_at ||
      null,
    reason_summary: getRemediationSummary(item) || getRemediationSummary(raw),
    finding_source: item.source || item.source_kind || item.sourceKind || raw.source || raw.source_kind || raw.sourceKind || "",
    stream: item.stream || raw.stream || rawRaw.stream || "",
    aws_context: awsContext,
    finding_metadata: findingMetadata,
    raw: item,
  };
}
